import { useCallback, useEffect, useRef, useState } from "react";

import { MAGAZINE_STATUS } from "../constants";
import { magazinePublishStrings } from "./publishStrings";

/**
 * "Publish magazine" in the RM Magazine Editor regenerates the magazine and
 * then files the print job. Digital Services refuses to print a render that is
 * superseded, a preview, not `DONE`, or without a PDF
 * (`assertOperatorPrintable`, codes `RM_PRINT_MAGAZINE_*`). The regeneration is
 * asynchronous (a lambda renders the PDF and marks the record `DONE`), so the
 * editor used to ask for the print while the new render was still `PENDING` —
 * and against the id of the render the regeneration had just superseded. Every
 * such send was refused.
 *
 * This module waits for the render the regeneration created to become
 * printable, and only then asks for the print, with that render's id.
 */

/** Same cadence the DMP uses to poll a generating magazine. */
export const GENERATION_POLL_INTERVAL_MS = 3000;

/** How long the editor waits before telling the operator to try again. */
export const GENERATION_TIMEOUT_MS = 3 * 60 * 1000;

/** The fields of a generated magazine this flow reads. */
export interface GeneratedMagazineSnapshot {
  id?: string;
  status?: string;
  isPreview?: boolean;
  active?: boolean;
  url?: string | null;
  coversOnlyUrl?: string | null;
}

export type GeneratedMagazineState =
  | "ready"
  | "pending"
  | "failed"
  | "superseded"
  | "alreadySent";

/**
 * Where a generated magazine stands, relative to the render this flow started.
 *
 * `ready` mirrors DS's `assertOperatorPrintable` exactly: active, not a
 * preview, `DONE`, and with a magazine or covers PDF. The lambda writes `DONE`
 * together with the PDF urls, so a `DONE` record without one (or a `DONE`
 * preview, which this flow never asks for) will not become printable by
 * waiting: that is a failed render, not a pending one.
 *
 * @param expectedId the id the regeneration returned. The read returns the
 * member's ACTIVE render, so a different id means a newer render replaced it.
 */
export const classifyGeneratedMagazine = (
  magazine: GeneratedMagazineSnapshot | undefined | null,
  expectedId?: string
): GeneratedMagazineState => {
  if (!magazine) {
    return "pending";
  }
  if (magazine.active === false || (expectedId && magazine.id && magazine.id !== expectedId)) {
    return "superseded";
  }
  if (magazine.status === MAGAZINE_STATUS.ERROR) {
    return "failed";
  }
  if (magazine.status === MAGAZINE_STATUS.SENT_FOR_PRINTING) {
    return "alreadySent";
  }
  if (magazine.status !== MAGAZINE_STATUS.DONE) {
    return "pending";
  }
  if (magazine.isPreview || (!magazine.url && !magazine.coversOnlyUrl) || !magazine.id) {
    return "failed";
  }
  return "ready";
};

export type GenerationWaitOutcome =
  | { kind: "ready"; magazine: GeneratedMagazineSnapshot & { id: string } }
  | { kind: "failed" | "superseded" | "alreadySent" | "timeout" | "aborted" };

const abortableSleep = (ms: number, signal?: AbortSignal) =>
  new Promise<void>((resolve) => {
    if (signal?.aborted) {
      resolve();
      return;
    }
    const timer = setTimeout(done, ms);
    function done() {
      clearTimeout(timer);
      signal?.removeEventListener("abort", done);
      resolve();
    }
    signal?.addEventListener("abort", done);
  });

/**
 * `promise`, or `undefined` once `ms` pass or `signal` aborts, whichever comes
 * first. The api instance sets no request timeout, so a read that never
 * answers would otherwise hold the poll (and the loader) past its deadline.
 */
const settleWithin = <T>(promise: Promise<T>, ms: number, signal?: AbortSignal) =>
  new Promise<T | undefined>((resolve, reject) => {
    const giveUp = () => finish(() => resolve(undefined));
    const timer = setTimeout(giveUp, ms);
    signal?.addEventListener("abort", giveUp);
    function finish(settle: () => void) {
      clearTimeout(timer);
      signal?.removeEventListener("abort", giveUp);
      settle();
    }
    if (signal?.aborted) {
      giveUp();
      return;
    }
    promise.then(
      (value) => finish(() => resolve(value)),
      (error) => finish(() => reject(error))
    );
  });

/**
 * Polls the member's generated magazine until it is printable, settles in a
 * state waiting cannot change, the timeout passes, or `signal` aborts. A failed
 * or unanswered read is treated as transient: the next poll tries again.
 */
export const waitForPrintableMagazine = async ({
  fetchGenerated,
  expectedId,
  signal,
  intervalMs = GENERATION_POLL_INTERVAL_MS,
  timeoutMs = GENERATION_TIMEOUT_MS,
}: {
  fetchGenerated: () => Promise<GeneratedMagazineSnapshot | undefined | null>;
  expectedId?: string;
  signal?: AbortSignal;
  intervalMs?: number;
  timeoutMs?: number;
}): Promise<GenerationWaitOutcome> => {
  const deadline = Date.now() + timeoutMs;

  for (;;) {
    if (signal?.aborted) {
      return { kind: "aborted" };
    }

    let magazine: GeneratedMagazineSnapshot | undefined | null;
    try {
      magazine = await settleWithin(fetchGenerated(), Math.max(0, deadline - Date.now()), signal);
    } catch {
      magazine = undefined;
    }

    if (signal?.aborted) {
      return { kind: "aborted" };
    }

    const state = classifyGeneratedMagazine(magazine, expectedId);
    if (state === "ready") {
      return { kind: "ready", magazine: magazine as GeneratedMagazineSnapshot & { id: string } };
    }
    if (state !== "pending") {
      return { kind: state };
    }
    if (Date.now() + intervalMs > deadline) {
      return { kind: "timeout" };
    }

    await abortableSleep(intervalMs, signal);
  }
};

/**
 * The regeneration request "Publish magazine" sends. Never a preview: DS never
 * prints a preview, and a non-preview render is also what updates the member's
 * digital magazine and landing page — what the editor's "Publish" promises, and
 * what the DMP member path sends on its last step.
 */
export const buildPublishGenerationRequest = <P>(routeParams: P) => ({
  payload: routeParams,
  isPreview: false as const,
});

/**
 * The id of the render a regeneration created. `generateReferralMagazineAdmin`
 * resolves with the axios response, not its body; accept either shape.
 */
export const generatedMagazineIdFrom = (response: unknown): string | undefined => {
  const value = response as { id?: unknown; data?: { id?: unknown } } | undefined;
  const id = value?.data?.id ?? value?.id;
  return typeof id === "string" && id ? id : undefined;
};

/** The member's generated magazine read may answer with one record or a list. */
export const firstGeneratedMagazine = (data: unknown): GeneratedMagazineSnapshot | undefined =>
  (Array.isArray(data) ? data[0] : data) as GeneratedMagazineSnapshot | undefined;

export type PublishPhase = "idle" | "generating" | "sending";

export interface PublishWhenGeneratedDeps {
  /** Starts a non-preview regeneration; resolves with the new render's id. */
  startGeneration: () => Promise<string | undefined>;
  /** Reads the member's active generated magazine for this issue. */
  fetchGenerated: () => Promise<GeneratedMagazineSnapshot | undefined | null>;
  /**
   * Files the print job for `generatedMagazineId`. A rejection is the caller's
   * to report: the editor's send-to-print mutation already toasts DS's refusal.
   */
  sendToPrint: (generatedMagazineId: string) => Promise<unknown>;
  /** Shows a message this flow produces itself (timeout, failed render...). */
  notify: (message: string) => void;
  intervalMs?: number;
  timeoutMs?: number;
}

const outcomeMessage: Record<"failed" | "superseded" | "alreadySent", string> = {
  failed: magazinePublishStrings.generationFailed,
  superseded: magazinePublishStrings.superseded,
  alreadySent: magazinePublishStrings.alreadySent,
};

/**
 * Regenerate, wait for the render to be printable, then send it to print.
 *
 * - One flow at a time: a repeat click while one runs does nothing, so the
 *   magazine is never regenerated or sent twice.
 * - After a timeout the started render is remembered, so the next `publish`
 *   resumes waiting on it instead of starting another regeneration. Call
 *   `forgetPendingGeneration` when the magazine is edited, so the next
 *   `publish` renders the edit.
 * - Unmounting stops the polling and nothing is sent afterwards.
 * - `phase` always returns to `idle` when a flow ends, however it ends.
 */
export const usePublishWhenGenerated = (deps: PublishWhenGeneratedDeps) => {
  const depsRef = useRef(deps);
  depsRef.current = deps;

  const [phase, setPhase] = useState<PublishPhase>("idle");
  const inFlightRef = useRef(false);
  const pendingGenerationRef = useRef<{ id?: string } | null>(null);
  const controllerRef = useRef<AbortController | null>(null);

  useEffect(
    () => () => {
      controllerRef.current?.abort();
    },
    []
  );

  const forgetPendingGeneration = useCallback(() => {
    pendingGenerationRef.current = null;
  }, []);

  const publish = useCallback(async () => {
    if (inFlightRef.current) {
      return;
    }
    inFlightRef.current = true;

    const controller = new AbortController();
    controllerRef.current = controller;
    const { signal } = controller;
    setPhase("generating");

    try {
      if (!pendingGenerationRef.current) {
        try {
          const id = await depsRef.current.startGeneration();
          pendingGenerationRef.current = { id };
        } catch {
          if (!signal.aborted) {
            depsRef.current.notify(magazinePublishStrings.couldNotStart);
          }
          return;
        }
      }

      const { intervalMs, timeoutMs } = depsRef.current;
      const outcome = await waitForPrintableMagazine({
        fetchGenerated: () => depsRef.current.fetchGenerated(),
        expectedId: pendingGenerationRef.current?.id,
        signal,
        intervalMs,
        timeoutMs,
      });

      if (outcome.kind === "aborted" || signal.aborted) {
        return;
      }
      if (outcome.kind === "timeout") {
        depsRef.current.notify(magazinePublishStrings.stillGenerating);
        return;
      }

      pendingGenerationRef.current = null;
      if (outcome.kind !== "ready") {
        depsRef.current.notify(outcomeMessage[outcome.kind]);
        return;
      }

      setPhase("sending");
      try {
        await depsRef.current.sendToPrint(outcome.magazine.id);
      } catch {
        // Reported by the caller (see `sendToPrint`).
      }
    } finally {
      inFlightRef.current = false;
      if (!signal.aborted) {
        setPhase("idle");
      }
    }
  }, []);

  return { phase, publish, forgetPendingGeneration };
};
